import { Artefact, ArtefactType, ArtefactDetectionConfig } from "@/types/artefacts";

import { BaseArtefactDetector } from "./BaseDetector";

/**
 * Detects graph visualisation data from JSON code blocks.
 *
 * Looks for ```json blocks containing a "graph" object with "nodes" and "edges"
 * arrays, as returned by the Neo4j MCP `visualise_graph` tool.
 */
export class GraphDetector extends BaseArtefactDetector {
  detect(content: string, messageId: string, _config: ArtefactDetectionConfig) {
    if (!this.validateContent(content)) {
      return this.createResult([], 0, ["Invalid content provided for graph detection"]);
    }

    const artefacts: Artefact[] = [];
    const warnings: string[] = [];
    let skippedCount = 0;
    let graphIndex = 0;

    // Match ```json code blocks
    const jsonBlockRegex = /```json\s*\n([\s\S]*?)```/g;
    let match;

    while ((match = jsonBlockRegex.exec(content)) !== null) {
      const rawJson = match[1].trim();

      try {
        const parsed = JSON.parse(rawJson);

        // Validate graph structure: must have graph.nodes and graph.edges arrays
        if (
          parsed &&
          parsed.graph &&
          Array.isArray(parsed.graph.nodes) &&
          Array.isArray(parsed.graph.edges)
        ) {
          const nodeCount = parsed.graph.nodes.length;
          const edgeCount = parsed.graph.edges.length;

          // Skip empty graphs
          if (nodeCount === 0) {
            skippedCount++;
            continue;
          }

          const title =
            typeof parsed.title === "string" && parsed.title.trim().length > 0
              ? parsed.title.trim()
              : `Graph Visualisation (${nodeCount} nodes, ${edgeCount} edges)`;

          const artefact: Artefact = {
            id: `${messageId}-graph-${graphIndex}`,
            type: ArtefactType.GRAPH,
            title,
            content: rawJson,
            messageId,
            timestamp: this.getCurrentTimestamp(),
            metadata: {
              fileExtension: "json",
              detectionPattern: "graph_json_block",
              estimatedSize: `${nodeCount} nodes, ${edgeCount} edges`,
              nodeCount,
              edgeCount,
            },
          };

          artefacts.push(artefact);
          graphIndex++;
        }
      } catch {
        // Not valid JSON — skip silently, this is expected for non-graph JSON blocks
      }
    }

    return this.createResult(artefacts, skippedCount, warnings);
  }
}
